import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useUserContext } from '../contexts/UserContext';
import { useAppContext } from '../contexts/AppContext';
import { useEquipmentContext } from '../contexts/EquipmentContext';
import { useRegionAndLocationContext } from '../contexts/RegionAndLocationContext';
import { LOCAL_STORAGE_KEYS } from '../utils/constants';
import { signOut } from 'aws-amplify/auth';
import { authClient } from '../utils/amplifyClient';
import { listDiscrepancies } from '../graphql/queries';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';
import { useNavItems } from '../hooks/useNavItems';
import { useCompanyLogo } from '../hooks/useCompanyLogo';
import { LeftNavigationView } from './LeftNavigationView';

export const LeftNavigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { company, companyUser, companyOwner, setCompanyUser, setCompanyOwner, setBusinessAdmin, setCompany } =
        useUserContext();
    const { resetEquipment } = useEquipmentContext();
    const { resetRegionAndLocation } = useRegionAndLocationContext();
    const { issueCount, setIssueCount, inboxCount, setInboxCount, setIsSideNavOpen } = useAppContext();

    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const companyLogo = useCompanyLogo();

    const navState = useNavItems();
    const { tryNavigate } = useNavigationGuard();

    useEffect(() => {
        if (company) {
            fetchNavBadgeCounts();
        }
    }, [company]);

    const fetchNavBadgeCounts = async () => {
        try {
            const issuesResponse = await authClient.graphql({
                query: listDiscrepancies,
                variables: { filter: { companyId: { eq: company?.id } } },
            });

            const discrepancies = issuesResponse.data.listDiscrepancies.items;
            setIssueCount(discrepancies.length);

            const criticalCount = discrepancies.filter((d: any) => d.status === 'inop').length;
            const warningCount = discrepancies.filter((d) => d.status === 'warn').length;
            const totalInboxItems = Math.min(criticalCount, 10) + Math.min(warningCount, 5);

            const userId = companyUser?.id || companyOwner?.id || '';
            let readCount = 0;
            try {
                const stored = localStorage.getItem(`inboxReadIds_${userId}`);
                if (stored) {
                    const readIds = new Set(JSON.parse(stored));
                    readCount = readIds.size;
                }
            } catch {
                /* ignore */
            }

            setInboxCount(Math.max(0, totalInboxItems - readCount));
        } catch (error) {
            console.error('Error fetching badge counts:', error);
            Sentry.captureException(error);
        }
    };

    const handleLogout = async () => {
        try {
            setCompanyUser(null);
            setCompany(null as any);
            setCompanyOwner(null);
            setBusinessAdmin(null);
            resetEquipment();
            resetRegionAndLocation();

            Object.values(LOCAL_STORAGE_KEYS).forEach((key) => {
                if (typeof key === 'string') localStorage.removeItem(key);
            });
            sessionStorage.clear();

            await signOut();
            setShowLogoutModal(false);
            navigate('/', { replace: true });
            window.location.reload();
        } catch (error) {
            console.error('Error during logout:', error);
            window.location.reload();
        }
    };

    return (
        <LeftNavigationView
            companyName={navState.companyName || 'TotalEquipIQ'}
            companyLogoUrl={companyLogo}
            userName={navState.userName}
            sections={navState.sections}
            role={navState.role}
            roleBadge={navState.roleBadge}
            banner={navState.banner}
            pathname={location.pathname}
            badgeCounts={{ issues: issueCount, inbox: inboxCount }}
            onNavigate={(href) => tryNavigate(href)}
            onLogoutClick={() => {
                setIsSideNavOpen(true);
                setShowLogoutModal(true);
            }}
            onAccountSettingsClick={() => tryNavigate('/settings')}
            showLogoutModal={showLogoutModal}
            onCancelLogout={() => setShowLogoutModal(false)}
            onConfirmLogout={handleLogout}
        />
    );
};
